import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfect for small projects and prototypes',
      price: { monthly: 0, yearly: 0 },
      badge: 'Free Forever',
      badgeColor: 'bg-accent/10 text-accent border-accent/20',
      features: [
        '10,000 messages/month',
        '5 channels',
        'Basic WebSocket support',
        'Community support',
        'Standard security',
        'API documentation access'
      ],
      limitations: [
        'No custom integrations',
        'Limited analytics',
        'No SLA guarantee'
      ],
      cta: 'Get Started Free',
      ctaVariant: 'outline',
      popular: false
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Ideal for growing businesses and teams',
      price: { monthly: 99, yearly: 990 },
      badge: 'Most Popular',
      badgeColor: 'bg-primary/10 text-primary border-primary/20',
      features: [
        '1M messages/month',
        'Unlimited channels',
        'Advanced WebSocket features',
        'Priority email support',
        'Enhanced security',
        'Advanced analytics',
        'Custom integrations',
        'Push notifications',
        'File sharing (100MB)',
        'Message history (1 year)'
      ],
      limitations: [],
      cta: 'Start Free Trial',
      ctaVariant: 'default',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'For large-scale applications and enterprises',
      price: { monthly: 'Custom', yearly: 'Custom' },
      badge: 'Contact Sales',
      badgeColor: 'bg-secondary/10 text-secondary border-secondary/20',
      features: [
        'Unlimited messages',
        'Unlimited channels',
        'Full feature access',
        '24/7 phone & email support',
        'Enterprise security (SOC 2)',
        'Custom analytics & reporting',
        'White-label options',
        'Dedicated infrastructure',
        'Custom file limits',
        'Unlimited message history',
        'SLA guarantee (99.9%)',
        'Custom integrations',
        'On-premise deployment'
      ],
      limitations: [],
      cta: 'Contact Sales',
      ctaVariant: 'secondary',
      popular: false
    }
  ];

  const additionalFeatures = [
    {
      category: 'Security & Compliance',
      items: [
        'End-to-end encryption',
        'SOC 2 Type II compliance',
        'GDPR compliance',
        'HIPAA compliance (Enterprise)',
        'Single Sign-On (SSO)',
        'Two-factor authentication'
      ]
    },
    {
      category: 'Developer Tools',
      items: [
        'REST APIs',
        'WebSocket APIs',
        'SDKs for 10+ languages',
        'Webhooks',
        'API rate limiting',
        'Sandbox environment'
      ]
    },
    {
      category: 'Infrastructure',
      items: [
        'Global CDN',
        'Auto-scaling',
        '15+ global regions',
        'Load balancing',
        'DDoS protection',
        'Monitoring & alerts'
      ]
    }
  ];

  const getPrice = (plan) => {
    if (typeof plan?.price?.[billingCycle] === 'string') {
      return plan?.price?.[billingCycle];
    }
    return plan?.price?.[billingCycle];
  };

  const getSavings = (plan) => {
    if (billingCycle === 'yearly' && typeof plan?.price?.yearly === 'number' && plan?.price?.yearly > 0) {
      const monthlyCost = plan?.price?.monthly * 12;
      const savings = ((monthlyCost - plan?.price?.yearly) / monthlyCost * 100)?.toFixed(0);
      return `Save ${savings}%`;
    }
    return null;
  };

  return (
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Icon name="DollarSign" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
              Transparent Pricing
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Scale with confidence
            <span className="block text-primary">Pay as you grow</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Start free and upgrade when you need more. No hidden fees, no surprises. 
            All plans include our core messaging features.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-muted/50 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                billingCycle === 'monthly' ?'bg-background text-foreground shadow-elevation-1' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                billingCycle === 'yearly' ?'bg-background text-foreground shadow-elevation-1' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs bg-accent/20 text-accent px-2 py-1 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {plans?.map((plan) => (
            <div
              key={plan?.id}
              className={`relative bg-card border rounded-xl p-8 ${
                plan?.popular
                  ? 'border-primary shadow-elevation-3 scale-105'
                  : 'border-border hover:border-primary/30'
              } transition-all duration-300`}
            >
              {/* Badge */}
              {plan?.badge && (
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border mb-6 ${plan?.badgeColor}`}>
                  {plan?.badge}
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  {plan?.name}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {plan?.description}
                </p>

                {/* Price */}
                <div className="flex items-baseline">
                  {typeof getPrice(plan) === 'string' ? (
                    <span className="text-4xl font-bold text-foreground">
                      {getPrice(plan)}
                    </span>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-foreground">
                        ${getPrice(plan)?.toLocaleString()}
                      </span>
                      <span className="text-muted-foreground ml-2">
                        /{billingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    </>
                  )}
                </div>
                
                {getSavings(plan) && (
                  <div className="text-sm text-accent font-medium mt-2">
                    {getSavings(plan)}
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">
                  What's included
                </h4>
                <ul className="space-y-3">
                  {plan?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Icon name="Check" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan?.limitations?.length > 0 && (
                  <div className="mt-6">
                    <h5 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                      Limitations
                    </h5>
                    <ul className="space-y-2">
                      {plan?.limitations?.map((limitation, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Icon name="X" size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-muted-foreground">{limitation}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* CTA */}
              <Button
                variant={plan?.ctaVariant}
                size="lg"
                fullWidth
                iconName={plan?.id === 'enterprise' ? 'MessageSquare' : 'ArrowRight'}
                iconPosition="right"
                onClick={() => {
                  if (plan?.id === 'enterprise') {
                    window.location.href = '/contact-sales';
                  } else {
                    window.location.href = '/developer-portal';
                  }
                }}
                className={plan?.popular ? 'shadow-elevation-2' : ''}
              >
                {plan?.cta}
              </Button>
            </div>
          ))}
        </div>

        {/* Additional Features */}
        <div className="bg-card/50 border border-border rounded-xl p-8">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            All plans include these powerful features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalFeatures?.map((category, index) => (
              <div key={index}>
                <h4 className="text-lg font-semibold text-foreground mb-4">
                  {category?.category}
                </h4>
                <ul className="space-y-2">
                  {category?.items?.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center space-x-2">
                      <Icon name="Check" size={14} className="text-accent flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="text-center mt-16">
          <h3 className="text-2xl font-bold text-foreground mb-4">
            Questions about pricing?
          </h3>
          <p className="text-muted-foreground mb-6">
            Our sales team is here to help you find the perfect plan for your needs.
          </p>
          <Button
            variant="outline"
            size="lg"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={() => window.location.href = '/contact-sales'}
          >
            Contact Sales Team
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;